import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useNotify } from '../../lib/notify';
import { downloadExport, type DataColumn, type DataModel, type FileFormat } from './api';
import { loadTemplates, saveTemplates, type ExportTemplate } from './templates';

const DEFAULT = '__default';
const ALL = '__all';

/** The columns picked when nothing else is chosen: the External ID and every plain value. */
const defaultFields = (columns: DataColumn[]) =>
  columns.filter((c) => c.kind !== 'ref_id').map((c) => c.path);

/** Columns grouped as on the file: the record's own, then each child list. */
function groupsOf(model: DataModel) {
  const own = model.columns.filter((c) => !c.child);
  const children = model.children.map((ch) => ({
    key: ch.key,
    label: ch.label,
    columns: model.columns.filter((c) => c.child === ch.key),
  }));
  return [{ key: '', label: model.label, columns: own }, ...children].filter(
    (g) => g.columns.length > 0,
  );
}

/**
 * Export a model to Excel or CSV: pick the columns (or a saved set of them) and the format. With
 * `ids`, only those records are exported.
 */
export function ExportDialog({
  model,
  ids,
  open,
  onClose,
}: {
  model: DataModel;
  ids?: string[];
  open: boolean;
  onClose: () => void;
}) {
  const { me } = useAuth();
  const notify = useNotify();
  const phone = useMediaQuery(useTheme().breakpoints.down('sm'));
  const scope = `${me?.tenant.id ?? ''}.${me?.user.id ?? ''}`;
  const [templates, setTemplates] = useState<ExportTemplate[]>(() =>
    loadTemplates(scope, model.name),
  );
  const [chosen, setChosen] = useState(DEFAULT);
  const [fields, setFields] = useState<Set<string>>(() => new Set(defaultFields(model.columns)));
  const [format, setFormat] = useState<FileFormat>('xlsx');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const groups = useMemo(() => groupsOf(model), [model]);

  const pick = (value: string) => {
    setChosen(value);
    if (value === DEFAULT) setFields(new Set(defaultFields(model.columns)));
    else if (value === ALL) setFields(new Set(model.columns.map((c) => c.path)));
    else {
      const t = templates.find((x) => x.name === value);
      const known = new Set(model.columns.map((c) => c.path));
      if (t) setFields(new Set(t.fields.filter((f) => known.has(f))));
    }
  };

  const toggle = (path: string) => {
    const next = new Set(fields);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    setFields(next);
  };

  const ordered = model.columns.filter((c) => fields.has(c.path)).map((c) => c.path);

  const store = (next: ExportTemplate[]) => {
    setTemplates(next);
    saveTemplates(scope, model.name, next);
  };
  const saveCurrent = () => {
    const name = newName.trim();
    if (!name) return;
    store([...templates.filter((t) => t.name !== name), { name, fields: ordered }]);
    setChosen(name);
    setNewName('');
    notify.success(`Saved "${name}"`);
  };
  const removeChosen = () => {
    store(templates.filter((t) => t.name !== chosen));
    pick(DEFAULT);
  };

  const run = () => {
    setBusy(true);
    downloadExport(model.name, { fields: ordered, ids, format })
      .then(onClose)
      .catch(notify.error)
      .finally(() => setBusy(false));
  };

  const isSaved = chosen !== DEFAULT && chosen !== ALL;
  const scopeText = ids?.length
    ? `${String(ids.length)} selected ${ids.length === 1 ? 'record' : 'records'}`
    : `every ${model.label.toLowerCase()} record you can see`;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={phone}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Export {model.label.toLowerCase()}</DialogTitle>
      <DialogContent dividers>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Exports {scopeText}. Keep the External ID column to update the same records by importing
          the file back.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            label="Columns"
            value={chosen}
            onChange={(e) => pick(e.target.value)}
            sx={{ flex: 1 }}
          >
            <MenuItem value={DEFAULT}>Standard columns</MenuItem>
            <MenuItem value={ALL}>All columns, with External IDs of related records</MenuItem>
            {templates.map((t) => (
              <MenuItem key={t.name} value={t.name}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>
          {isSaved && (
            <Tooltip title="Delete this saved set">
              <IconButton aria-label="Delete saved columns" onClick={removeChosen}>
                <DeleteOutlineRounded />
              </IconButton>
            </Tooltip>
          )}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={format}
            onChange={(_, v: FileFormat | null) => v && setFormat(v)}
            aria-label="File format"
          >
            <ToggleButton value="xlsx">Excel</ToggleButton>
            <ToggleButton value="csv">CSV</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {groups.map((g) => (
          <Box key={g.key} sx={{ mb: 1.5 }}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2">{g.label}</Typography>
              <Button
                size="small"
                onClick={() => {
                  const paths = g.columns.map((c) => c.path);
                  const allOn = paths.every((p) => fields.has(p));
                  const next = new Set(fields);
                  paths.forEach((p) => (allOn ? next.delete(p) : next.add(p)));
                  setFields(next);
                }}
              >
                {g.columns.every((c) => fields.has(c.path)) ? 'None' : 'All'}
              </Button>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                columnGap: 1,
              }}
            >
              {g.columns.map((c) => (
                <FormControlLabel
                  key={c.path}
                  control={
                    <Checkbox
                      size="small"
                      checked={fields.has(c.path)}
                      onChange={() => toggle(c.path)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {g.key ? c.label.slice(g.label.length + 3) : c.label}
                      {c.required && (
                        <Typography component="span" color="error" sx={{ ml: 0.25 }}>
                          *
                        </Typography>
                      )}
                    </Typography>
                  }
                />
              ))}
            </Box>
          </Box>
        ))}

        <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Save these columns as"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveCurrent();
            }}
            sx={{ flex: 1 }}
            slotProps={{ htmlInput: { maxLength: 60 } }}
          />
          <Button variant="outlined" onClick={saveCurrent} disabled={!newName.trim()}>
            Save
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" loading={busy} disabled={ordered.length === 0} onClick={run}>
          Export {String(ordered.length)} {ordered.length === 1 ? 'column' : 'columns'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
